import { setLastSignedInUser } from '@root/utils/cookies/cookies';
import mixpanel from 'mixpanel-browser';
import getConfig from 'next/config';
import { shouldDisableTrackingPersistance } from 'pages/_document';
import { OSType } from 'scribe-web-shared/enums';
import { getOS } from 'scribe-web-shared/functions';

import { hasSystemAdminRole } from '../components/auth/withRBAC';
import { CursiveRecorder, isElectron } from './electron';
import isBusinessEmail from './isBusinessEmail';
import { trackNewRelicLog } from './tracking';
import { mapUUIDToBucket } from './uuidHelpers';

const { publicRuntimeConfig } = getConfig();

mixpanel.init(publicRuntimeConfig.mixpanelProjectToken, {
  disable_persistence: shouldDisableTrackingPersistance?.(),
  api_host: publicRuntimeConfig.analyticsProxyUrl,
  ignore_dnt: true,
  error_reporter: (message, error) => {
    trackNewRelicLog('mixpanel_error', null, { message, error });
  }
});

const GHOSTWRITER_USER_ID = 'c85193aa-c2c6-45a4-a621-e50438e60d2a';
const SCRIBE_ORG_ID = '18017dee-ec83-4829-9e78-3e0b2586e96e';

const getUserRole = (user) => {
  const role = user?.active_organization?.super_organization?.role;

  if (!role) {
    return 'Unknown';
  }
  if (hasSystemAdminRole(role)) {
    return 'System Admin';
  }
  return 'Creator';
};

export const getCreatorType = (file) => {
  if (file?.user_owner?.id === GHOSTWRITER_USER_ID) {
    return 'ghostwriter';
  }
  if (file?.organization_owner?.id === SCRIBE_ORG_ID || file?.author?.endsWith(' from Scribe')) {
    return 'employee';
  }
  return 'other';
};

const shouldTrack =
  process.env.NODE_ENV === 'production' &&
  (!global.window || (global.window.location.origin && !global.window.location.origin.includes('localhost')));

export const getTimestampFromDate = (date) => {
  if (date) {
    return Math.round(Date.parse(date) / 1000);
  }
  return null;
};

const createMixpanelUser = (user, recordingDetails, onboardingAnswers = null) => {
  const bucket = user ? mapUUIDToBucket(user.id) : undefined;
  const mixpanelUser = {
    USER_ID: user?.id,
    'Sign up date': user?.date_joined,
    'First name': user?.first_name,
    'Last name': user?.last_name,
    bucket,
    Role: user?.role,
    $email: user?.email,
    business_email: isBusinessEmail(user?.email),
    super_organization_id: user?.active_organization?.super_organization?.id,
    plan: user?.active_organization?.super_organization?.plan.name,
    subscription_status: user?.active_organization?.super_organization?.stripe_subscription_status,
    installed_extension: user?.installed_extension,
    is_enterprise_self_serve: user?.active_organization?.super_organization?.enterprise_plan_type === 'self_serve',
    active_super_organization_role: getUserRole(user),
    extension_currently_pinned: user?.extension_currently_pinned,
    pro_plan_type: user?.active_organization?.super_organization?.pro_plan_type,
    active_team_size: user?.active_organization?.user_count,
    signup_method: user?.signup_method,
    ...onboardingAnswers
  };

  if (user?.signup_source) {
    mixpanelUser.signup_source = user.signup_source;
  }
  if (getOS() === OSType.iOS || getOS() === OSType.Android) {
    mixpanelUser.signup_device = 'mobile';
  } else {
    mixpanelUser.signup_device = 'desktop';
  }
  if (isElectron() && CursiveRecorder().getRecorderDetails) {
    Object.assign(mixpanelUser, recordingDetails || CursiveRecorder().getRecorderDetails());
  }

  return mixpanelUser;
};

export const trackUser = (user, onboardingData = null, recordingDetails = null) => {
  setLastSignedInUser(user);
  const mixpanelUser = createMixpanelUser(user, recordingDetails, onboardingData);

  if (shouldTrack) {
    mixpanel.identify(user.id);
    mixpanel.people.set(mixpanelUser);
  } else {
    if (!user) {
      console.warn('Null user passed to tracking code.');
    }
    console.log(`Would track user: ${JSON.stringify(mixpanelUser)} in production. Created at ${getTimestampFromDate(user.date_joined)}`);
  }
};
